import { DateRange } from "design-system/molecules/DatePicker"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { Headset, MessageCircleQuestion, Unlink2 } from "lucide-react"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CanonicalTopicsTimeline } from "modules/insights/components/CanonicalTopicsTimeline"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Route as SupportRoute, SupportTabSchema } from "routes/_base/_auth/support/list/route"
import { ClusterDomainEnum, ClusterDomainSchema } from "schema"
import z from "zod"
import { ActionRequests } from "./ActionRequests"
import { ConversationQuestions } from "./ConversationQuestions"
import { ConversationTickets } from "./ConversationTickets"
import { Issues } from "./Issues"
import { NewQuestions } from "./NewQuestions"
import { SupportFilter } from "./SupportFilter"

const LEGACY_SUPPORT_TABS = ["tickets", "questions"] as const
const NEW_SUPPORT_TABS = ["newQuestions", "actionRequests", "issues"] as const

type SupportTab = z.infer<typeof SupportTabSchema>

export function Support() {
  const { t } = useTranslation()
  const { developerMode } = useDeveloperMode()
  const { isEnabled: isNewSupportFlowEnabled } = useNewSupportFlow()
  const isAmphoraCompany = useIsAmphoraCompany()

  const navigate = SupportRoute.useNavigate()
  const search = SupportRoute.useSearch()

  const requestedTab = search.supportTab
  const shouldShowNewSupportTabs = isNewSupportFlowEnabled || developerMode
  const fallbackTab = isNewSupportFlowEnabled ? "issues" : "tickets"
  const isRequestedTabUnavailable =
    (isNewSupportFlowEnabled && isLegacySupportTab(requestedTab)) ||
    (!shouldShowNewSupportTabs && isNewSupportTab(requestedTab))
  const currentTab = isRequestedTabUnavailable ? fallbackTab : requestedTab
  const clusterDomain = getClusterDomainForSupportTab(currentTab)

  useEffect(() => {
    if (!isRequestedTabUnavailable) return

    const filterBy = { ...search.filterBy }
    delete filterBy.canonicalId
    if (fallbackTab !== "tickets") {
      delete filterBy.humanReply
      delete filterBy.unansweredCustomerReply
    }

    navigate({
      search: {
        ...search,
        supportTab: fallbackTab,
        page: 1,
        filterBy: Object.keys(filterBy).length > 0 ? filterBy : undefined,
      },
    })
  }, [fallbackTab, isRequestedTabUnavailable, navigate, search])

  function onApplyClusterFilters({
    domain: _domain,
    segmentId,
    canonicalId,
    dateRange,
  }: {
    domain: z.infer<typeof ClusterDomainSchema> | undefined
    segmentId: string | undefined
    canonicalId: string | undefined
    dateRange: DateRange | undefined
  }) {
    const newFilterBy = { ...search.filterBy }

    if (segmentId) newFilterBy.segmentId = [segmentId]
    else delete newFilterBy.segmentId

    if (canonicalId) newFilterBy.canonicalId = [canonicalId]
    else delete newFilterBy.canonicalId

    if (dateRange && dateRange.from) {
      newFilterBy.dateRange = {
        from: dateRange.from,
        to: dateRange.to,
      }
    } else {
      delete newFilterBy.dateRange
    }

    navigate({ search: { ...search, filterBy: newFilterBy } })
  }

  function onTabChange(tab: SupportTab) {
    const newFilterBy = { ...search.filterBy }
    delete newFilterBy.canonicalId
    if (tab !== "tickets") {
      delete newFilterBy.humanReply
      delete newFilterBy.unansweredCustomerReply
    }

    navigate({
      search: {
        ...search,
        supportTab: tab,
        page: 1,
        filterBy: Object.keys(newFilterBy).length > 0 ? newFilterBy : undefined,
      },
    })
  }

  return (
    <div className="flex flex-col gap-6 w-full h-full">
      <Tabs value={currentTab} onValueChange={(val) => onTabChange(val as SupportTab)}>
        <TabsList className="max-w-full overflow-x-auto">
          {!isNewSupportFlowEnabled && (
            <>
              <TabsTrigger value="tickets">
                <Headset size={20} />
                <span>{t("support.tabs.tickets")}</span>
              </TabsTrigger>
              <TabsTrigger value="questions">
                <MessageCircleQuestion size={20} />
                <span>{t("support.tabs.questions")}</span>
              </TabsTrigger>
            </>
          )}
          {shouldShowNewSupportTabs && (
            <>
              <TabsTrigger value="issues">
                <Headset size={20} />
                <span>{t("support.tabs.issues")}</span>
              </TabsTrigger>
              <TabsTrigger value="newQuestions">
                <MessageCircleQuestion size={20} />
                <span>{t("support.tabs.questions")}</span>
              </TabsTrigger>
              <TabsTrigger value="actionRequests">
                <Unlink2 size={20} />
                <span>{t("support.tabs.actionRequests")}</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>
      </Tabs>
      <CanonicalTopicsTimeline
        key={clusterDomain}
        domain={clusterDomain}
        availableDomains={[clusterDomain]}
        segmentId={search.filterBy?.segmentId?.[0]}
        canonicalId={search.filterBy?.canonicalId?.[0]}
        dateRange={search.filterBy?.dateRange}
        groupNonCategoryTopics={isAmphoraCompany}
        onApplyFilters={onApplyClusterFilters}
      />
      <div className="flex flex-col gap-4">
        <SupportFilter
          showStatus={currentTab === "tickets"}
          showCanonicalTopic
          currentTab={currentTab}
          clusterDomain={clusterDomain}
        />
        <SupportList fullPath={SupportRoute.fullPath} search={search} currentTab={currentTab} />
      </div>
    </div>
  )
}

function SupportList({
  fullPath,
  search,
  currentTab,
}: {
  fullPath: string
  search: ReturnType<typeof SupportRoute.useSearch>
  currentTab: SupportTab
}) {
  switch (currentTab) {
    case "tickets":
      return <ConversationTickets fullPath={fullPath} search={search} />
    case "questions":
      return <ConversationQuestions fullPath={fullPath} search={search} />
    case "newQuestions":
      return <NewQuestions fullPath={fullPath} search={search} />
    case "actionRequests":
      return <ActionRequests fullPath={fullPath} search={search} />
    case "issues":
      return <Issues fullPath={fullPath} search={search} />
  }
}

function getClusterDomainForSupportTab(tab: SupportTab) {
  switch (tab) {
    case "tickets":
    case "questions":
      return ClusterDomainEnum.Support
    case "newQuestions":
      return ClusterDomainEnum.Question
    case "actionRequests":
      return ClusterDomainEnum.ActionRequest
    case "issues":
      return ClusterDomainEnum.Issue
  }
}

function isLegacySupportTab(tab: SupportTab): tab is (typeof LEGACY_SUPPORT_TABS)[number] {
  return LEGACY_SUPPORT_TABS.includes(tab as (typeof LEGACY_SUPPORT_TABS)[number])
}

function isNewSupportTab(tab: SupportTab): tab is (typeof NEW_SUPPORT_TABS)[number] {
  return NEW_SUPPORT_TABS.includes(tab as (typeof NEW_SUPPORT_TABS)[number])
}
